import React from 'react';
import { useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
} from '../../util/data';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { isUserAuthorized } from '../../util/userHelpers';

import { ResponsiveImage, IconBannedUser, NamedLink } from '../../components/';

import css from './ProviderAvatar.module.css';

// Responsive image sizes for provider card variants
const PROVIDER_AVATAR_SIZES_SMALL = '160px';
const PROVIDER_AVATAR_SIZES_MEDIUM = '180px';
const PROVIDER_AVATAR_SIZES_LARGE = '200px';

const PROVIDER_AVATAR_IMAGE_VARIANTS = [
  // 160x160
  'square-small',

  // 320x320
  'square-small2x',

  // 640x640
  'scaled-medium',

  // 1280x1280
  'scaled-large',

  'landscape-crop',

  'default',
];

/**
 * ProviderAvatar Component
 *
 * Avatar component specifically designed for provider cards.
 * Displays profile images in a card layout with full-width responsive sizing.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object?} props.user API entity
 * @param {string} props.variant - 'small', 'medium', or 'large' (default: 'medium')
 * @param {boolean} props.disableProfileLink
 * @returns {JSX.Element} provider avatar
 */
export const ProviderAvatar = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    user,
    variant = 'medium',
    disableProfileLink,
  } = props;

  const variantClass = {
    small: css.smallVariant,
    medium: css.mediumVariant,
    large: css.largeVariant,
  }[variant] || css.mediumVariant;

  const renderSizes = {
    small: PROVIDER_AVATAR_SIZES_SMALL,
    medium: PROVIDER_AVATAR_SIZES_MEDIUM,
    large: PROVIDER_AVATAR_SIZES_LARGE,
  }[variant] || PROVIDER_AVATAR_SIZES_MEDIUM;

  const classes = classNames(rootClassName || css.root, variantClass, className);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);
  const isUnauthorizedUser = userIsCurrentUser && !isUserAuthorized(user);
  const profileVariant =
    user?.attributes?.state === 'pendingApproval'
      ? PROFILE_PAGE_PENDING_APPROVAL_VARIANT
      : user?.attributes?.state;

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const displayName = userDisplayNameAsString(avatarUser, '');
  const rootProps = { className: classes, title: displayName };
  const linkProps =
    isUnauthorizedUser && avatarUser.id
      ? {
          name: 'ProfilePageVariant',
          params: { id: avatarUser.id.uuid, variant: profileVariant },
        }
      : avatarUser.id
      ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
      : { name: 'ProfileBasePage' };
  const hasProfileImage = avatarUser.profileImage && avatarUser.profileImage.id;
  const profileLinkEnabled = !disableProfileLink;

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <IconBannedUser className={css.bannedUserIcon} />
      </div>
    );
  } else if (hasProfileImage && profileLinkEnabled) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          variants={PROVIDER_AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
      </NamedLink>
    );
  } else if (hasProfileImage) {
    return (
      <div {...rootProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarUser.profileImage}
          variants={PROVIDER_AVATAR_IMAGE_VARIANTS}
          sizes={renderSizes}
        />
      </div>
    );
  } else if (profileLinkEnabled) {
    // Placeholder for no image
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <div className={css.placeholder} />
      </NamedLink>
    );
  } else {
    // Placeholder for no image
    return (
      <div {...rootProps}>
        <div className={css.placeholder} />
      </div>
    );
  }
};

export default ProviderAvatar;

// Provider Avatar Variants optimized for card layouts

export const ProviderAvatarSmall = props => (
  <ProviderAvatar
    rootClassName={css.rootSmall}
    variant="small"
    {...props}
  />
);
ProviderAvatarSmall.displayName = 'ProviderAvatarSmall';

export const ProviderAvatarMedium = props => (
  <ProviderAvatar
    rootClassName={css.rootMedium}
    variant="medium"
    {...props}
  />
);
ProviderAvatarMedium.displayName = 'ProviderAvatarMedium';

export const ProviderAvatarLarge = props => (
  <ProviderAvatar
    rootClassName={css.rootLarge}
    variant="large"
    {...props}
  />
);
ProviderAvatarLarge.displayName = 'ProviderAvatarLarge';
